import axiosInstance from './axiosInstance';

// ================= TYPES & INTERFACES =================

// Format Response chuẩn chung của hệ thống
export interface ApiResponse<T = any> {
    statusCode: number;
    data: T;
    message: string;
}

export interface PaginatedData<T> {
    totalItem: number;
    totalPage: number;
    page: number;
    items: T[];
}

export interface QueryParams {
    page?: number;
    pageSize?: number;
    filters?: string | any; // Định dạng tùy thuộc vào hàm buildQueryPrisma (vd: field:operator:value)
}

// ================= 1. AUTH API =================
export const AuthApi = {
    login: (data: any): Promise<ApiResponse<any>> => {
        return axiosInstance.post('/auth/login', data);
    },
    register: (data: any): Promise<ApiResponse<boolean>> => {
        return axiosInstance.post('/auth/register', data);
    },
    getInfo: (): Promise<ApiResponse<any>> => {
        return axiosInstance.get('/auth/get-info');
    },
    // Lưu ý: refresh-token thường tự động gọi qua interceptor, nhưng bạn vẫn có thể gọi thủ công nếu cần
    refreshToken: (): Promise<ApiResponse<any>> => {
        return axiosInstance.post('/auth/refresh-token');
    },
    logout: (): Promise<ApiResponse<any>> => {
        return axiosInstance.post('/auth/logout');
    },
};

// ================= 2. PRODUCT API =================
export const ProductApi = {
    getAll: (params?: QueryParams): Promise<ApiResponse<PaginatedData<any>>> => {
        return axiosInstance.get('/product', { params });
    },
    getById: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.get(`/product/${id}`);
    },
    create: (data: any): Promise<ApiResponse<any>> => {
        return axiosInstance.post('/product', data);
    },
    update: (id: number, data: any): Promise<ApiResponse<any>> => {
        return axiosInstance.put(`/product/${id}`, data);
    },
    delete: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.delete(`/product/${id}`);
    },
    uploadImage: (id: number, file: File): Promise<ApiResponse<string>> => {
        const formData = new FormData();
        formData.append('image', file);
        return axiosInstance.post(`/product/${id}/image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
};

// ================= 3. CATEGORY API =================
export const CategoryApi = {
    create: (data: { name: string }): Promise<ApiResponse<boolean>> => {
        return axiosInstance.post('/category', data);
    },
    getAll: (params?: QueryParams): Promise<ApiResponse<PaginatedData<any>>> => {
        return axiosInstance.get('/category', { params });
    },
    getById: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.get(`/category/${id}`, { data: { id } }); // Theo document có body {id}
    },
    update: (id: number, data: { name: string }): Promise<ApiResponse<boolean>> => {
        return axiosInstance.patch(`/category/${id}`, data);
    },
    delete: (id: number): Promise<ApiResponse<string>> => {
        return axiosInstance.delete(`/category/${id}`);
    },
};

// ================= 4. ORDER API =================
export const OrderApi = {
    create: (data: any): Promise<ApiResponse<boolean>> => {
        return axiosInstance.post('/order', data);
    },
    getAll: (params?: QueryParams): Promise<ApiResponse<PaginatedData<any>>> => {
        return axiosInstance.get('/order', { params });
    },
    getById: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.get(`/order/${id}`);
    },
    delete: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.delete(`/order/${id}`);
    },
};

// ================= 5. EMAIL/MESSAGE API =================
export const EmailApi = {
    create: (data: any): Promise<ApiResponse<any>> => {
        return axiosInstance.post('/email', data);
    },
    getAll: (params?: QueryParams): Promise<ApiResponse<PaginatedData<any>>> => {
        return axiosInstance.get('/email', { params });
    },
    getById: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.get(`/email/${id}`);
    },
    delete: (id: number): Promise<ApiResponse<any>> => {
        return axiosInstance.delete(`/email/${id}`);
    },
};
// ================= 6. QUANTITY API =================
export const QuantityApi = {
    getAll: (params?: QueryParams): Promise<ApiResponse<PaginatedData<any>>> => {
        return axiosInstance.get('/quantity', { params });
    },
};

// ================= AI API =================
export const AiApi = {
    recommend: (data: { prompt: string }): Promise<ApiResponse<any[]>> => {
        return axiosInstance.post('/ai-recommend', data);
    },
};